import type { ReactNode } from 'react';
import { useT } from '@skm/ui';
import type { UiLocale } from '@skm/i18n';
import type { Principal } from '../auth/session.ts';
import { buildNav } from './nav.ts';

export interface ShellProps {
  readonly principal: Principal;
  readonly locale: UiLocale;
  readonly onLocaleChange: (l: UiLocale) => void;
  readonly onNavigate: (route: string) => void;
  readonly onSignOut: () => void;
  /** Enabled feature flags of `/users/me`; a screen behind a flag (the report assistant) is in the rail only when it is on. */
  readonly featureFlags?: ReadonlySet<string>;
  readonly children?: ReactNode;
}

/** App shell: rail from the screen registry (permission-filtered), top bar with locale switch and user [02 §18]. */
export function Shell({
  principal,
  locale,
  onLocaleChange,
  onNavigate,
  onSignOut,
  featureFlags,
  children,
}: ShellProps) {
  const t = useT();
  const nav = buildNav(principal.permissions, featureFlags);
  return (
    <div className="skm-shell">
      <header className="skm-topbar">
        <span>{t('frame.app.name')}</span>
        <label>
          {t('frame.locale.label')}
          <select
            value={locale}
            onChange={(e) => onLocaleChange(e.target.value === 'fr' ? 'fr' : 'en')}
          >
            <option value="en">{t('frame.locale.en')}</option>
            <option value="fr">{t('frame.locale.fr')}</option>
          </select>
        </label>
        <span>{principal.displayName}</span>
        <button type="button" onClick={onSignOut}>
          {t('frame.signOut')}
        </button>
      </header>
      <nav aria-label={t('frame.nav.label')} className="skm-rail">
        {nav.map((sec) => (
          <section key={sec.section}>
            <h2>{t(`frame.nav.section.${sec.section}`)}</h2>
            <ul>
              {sec.screens.map((s) => (
                <li key={s.id}>
                  <a
                    href={s.route}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(s.route);
                    }}
                  >
                    {t(s.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
      <main className="skm-content">{children}</main>
    </div>
  );
}
